import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Briefcase, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import SupplierPicker from "@/components/SupplierPicker";
import { money, timeAgo } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", description: "", supplier_id: null, cost: "" };

function ServiceDrawer({ sid, onClose, suppliers, reloadSuppliers, onChange, canManage }) {
  const [s, setS] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/services/${sid}`).then((r) => { setS(r.data); setF({ name: r.data.name, description: r.data.description || "", supplier_id: r.data.supplier_id, cost: r.data.cost ?? "" }); });
  useEffect(() => { load(); }, [sid]);

  const patch = async (body) => {
    try { const { data } = await api.patch(`/services/${sid}`, body); setS(data); onChange?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveFields = async () => { await patch({ name: f.name, description: f.description, cost: f.cost === "" ? null : parseFloat(f.cost) }); toast.success("Saved"); };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/services/${sid}/notes`, { message: note }); setS(data); setNote(""); onChange?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/services/${sid}`); toast.success("Service deleted"); onChange?.(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const notes = [...(s?.notes || [])].reverse();

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto rounded-none border-l border-zinc-200 p-6 sm:max-w-xl">
        <SheetTitle className="sr-only">Service details</SheetTitle>
        <SheetDescription className="sr-only">Edit service details, supplier and notes</SheetDescription>
        {!s ? (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>
        ) : (
          <div className="space-y-5">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-[#e84e34]">Service</p>
              <h2 className="mt-1 font-heading text-xl font-bold tracking-tight text-zinc-900" data-testid="drawer-service-title">{s.name}</h2>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Name</label>
                <input data-testid="service-edit-name" value={f.name} onChange={(ev) => setF({ ...f, name: ev.target.value })} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Description</label>
                <textarea data-testid="service-edit-desc" value={f.description} onChange={(ev) => setF({ ...f, description: ev.target.value })} rows={2} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Cost / rate ($)</label>
                <input type="number" step="0.01" data-testid="service-edit-cost" value={f.cost} onChange={(ev) => setF({ ...f, cost: ev.target.value })} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              </div>
              <button onClick={saveFields} data-testid="service-save" className="bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800">Save details</button>
            </div>
            <SupplierPicker value={s.supplier_id} onChange={(id) => patch({ supplier_id: id })} suppliers={suppliers} reload={reloadSuppliers} />
            {s.supplier && <p className="text-xs text-zinc-500">Provided by <span className="font-medium text-zinc-700">{s.supplier.company_name}</span></p>}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">Notes</p>
              <div className="mb-2 space-y-2" data-testid="service-notes">
                {notes.length === 0 && <p className="text-xs text-zinc-400">No notes yet</p>}
                {notes.map((n) => (
                  <div key={n.id} className="border border-zinc-200 bg-zinc-50 p-2.5">
                    <p className="text-xs font-medium text-zinc-900">{n.user_name} <span className="font-normal text-zinc-400">· {timeAgo(n.created_at)}</span></p>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-700">{n.message}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <textarea data-testid="service-note-input" value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Add a note…" rows={2} className="flex-1 resize-none border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                <button onClick={addNote} disabled={busy} data-testid="service-note-submit" className="flex h-9 w-9 items-center justify-center bg-[#e84e34] text-white hover:bg-[#c73e26] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
              </div>
            </div>
            {canManage && <button onClick={remove} data-testid="service-delete" className="flex items-center gap-1 border border-zinc-200 px-3 py-1.5 text-xs text-zinc-500 hover:border-[#e84e34] hover:text-[#e84e34]"><Trash2 className="h-3.5 w-3.5" /> Delete service</button>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Services() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [services, setServices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const loadSuppliers = useCallback(async () => { const s = await api.get("/suppliers"); setSuppliers(s.data); }, []);
  const load = useCallback(async () => { const s = await api.get("/services"); setServices(s.data); setLoading(false); }, []);
  useEffect(() => { load(); loadSuppliers(); }, [load, loadSuppliers]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/services", { name: form.name, description: form.description, supplier_id: form.supplier_id, cost: form.cost === "" ? null : parseFloat(form.cost) });
      toast.success("Service created");
      setCreateOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this service? This cannot be undone.")) return;
    try { await api.delete(`/services/${id}`); toast.success("Service deleted"); setServices((prev) => prev.filter((x) => x.id !== id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-3">
        <p className="text-sm text-zinc-500">{services.length} services</p>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <button data-testid="create-service-button" className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#c73e26]"><Plus className="h-4 w-4" /> New Service</button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Add service</DialogTitle>
              <DialogDescription>Track services and the suppliers who provide them.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <input data-testid="service-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Service name" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              <textarea data-testid="service-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Cost / rate ($)</label>
                <input type="number" step="0.01" data-testid="service-cost-input" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0.00" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              </div>
              <SupplierPicker value={form.supplier_id} onChange={(id) => setForm({ ...form, supplier_id: id })} suppliers={suppliers} reload={loadSuppliers} />
            </div>
            <DialogFooter>
              <button data-testid="service-submit-button" onClick={create} disabled={saving} className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white hover:bg-[#c73e26] disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create service</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>
      ) : services.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm text-zinc-400">No services yet. Add your first one.</div>
      ) : (
        <div className="flex-1 overflow-auto p-6">
          <table className="w-full border-collapse border border-zinc-200 bg-white" data-testid="services-table">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3">Cost</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.id} data-testid={`service-row-${s.id}`} onClick={() => setOpenId(s.id)} className="cursor-pointer border-b border-zinc-100 transition-colors hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center border border-zinc-200 bg-zinc-50 text-zinc-300"><Briefcase className="h-4 w-4" /></div>
                      <div>
                        <div className="text-sm font-medium text-zinc-900">{s.name}</div>
                        <div className="truncate text-xs text-zinc-400" style={{ maxWidth: 280 }}>{s.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-600">{s.supplier?.company_name || "—"}</td>
                  <td className="px-4 py-3 text-sm text-zinc-700">{money(s.cost)}</td>
                  <td className="px-4 py-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                    {canManage && <button data-testid={`delete-service-row-${s.id}`} onClick={() => del(s.id)} title="Delete service" className="inline-flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-500 transition-colors hover:border-[#e84e34] hover:text-[#e84e34]"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <ServiceDrawer sid={openId} onClose={() => setOpenId(null)} suppliers={suppliers} reloadSuppliers={loadSuppliers} onChange={load} canManage={canManage} />}
    </div>
  );
}
